import { Card, CardContent, CardHeader, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { useDropzone } from 'react-dropzone';
import { Box } from '@material-ui/core';
import { Close, CloudDownload, CloudUpload } from '@material-ui/icons';
import { FC, SyntheticEvent } from 'react';
import { Button, IconButton } from '@roc/ui';
import Grid from '@material-ui/core/Grid';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { PersonalInformation } from './personalInformation';
import { useUserStore } from '@roc/feature-app-core';

const useFileUploadStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(2),
  },
  cardContent: {
    paddingTop: 0,
  },
  fileLine: {
    display: 'flex',
    alignItems: 'center',
  },
  dragArea: {
    border: '2px dotted #427ED1',
    width: '100%',
    backgroundColor: '#EAF4FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 20,
    borderRadius: 5,
    cursor: 'pointer',
  },
  icon: {
    minWidth: '20px',
    minHeight: '20px',
    width: '3vw',
    height: '3vw',
    color: '#4B89DA',
  },
  fileContainer: {
    border: '1px solid grey',
    backgroundColor: '#EAF4FA',
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '10px 0',
    borderRadius: 5,
  },

  fileName: {
    marginLeft: 5,
    marginRight: 2,
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  idOptions: {
    margin: theme.spacing(0, 0, 1),
    paddingLeft: theme.spacing(2.5),
  },
  removeButton: {
    marginLeft: theme.spacing(2),
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
    float: 'right',
  },
}));

export const IdInformationStep = observer(({ displayPersonalInformation = false }: { displayPersonalInformation?: boolean }) => {
  const { identityCheckStore } = useStore();
  const { idInformationForm } = identityCheckStore;
  const { userStore } = useUserStore();
  const { isNportalUser } = userStore;

  const classes = useFileUploadStyles();
  const uploadImageTitle = displayPersonalInformation ? undefined : "Download Driver's License Image";

  const handleDownload = () => {
    identityCheckStore.downloadDLFile();
  }

  return (
    <Grid container spacing={2}>
      {displayPersonalInformation && <PersonalInformation personalInformation={identityCheckStore.formInfo} />}
      <Grid item xs={12}>
        <Typography variant="h5">UPLOAD YOUR PHOTO ID</Typography>
      </Grid>
      <Grid item xs={12}>
        <Alert severity="info">
          <AlertTitle>Which ID should I upload?</AlertTitle>
          <ol className={classes.idOptions}>
            <li>US Driver's License (preferred) - valid and unexpired</li>
            <li>Passport - only if you don't have a driver's license</li>
          </ol>
          Make sure the image is clear, all four corners are visible, and the name matches the information above.
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <DriversLicenseUpload
          displayPersonalInformation={displayPersonalInformation}
          title={uploadImageTitle}
          file={identityCheckStore.driversLicense}
          onChange={file => identityCheckStore.setDriversLicense(file)}
          onDownload={identityCheckStore.formInfo?.driversLicensePath ? handleDownload : null}
          isNportalUser={isNportalUser}
        />
      </Grid>
    </Grid>
  );
});

interface DriversLicenseUploadProps {
  displayPersonalInformation: boolean;
  title?: string;
  file: File | null;
  onChange: (file: File | null) => void;
  onDownload: () => void;
  isNportalUser: boolean;
}

export const DriversLicenseUpload: FC<DriversLicenseUploadProps> = ({
  displayPersonalInformation,
  title,
  file,
  onChange,
  onDownload,
  isNportalUser,
}) => {
  function onDrop(newFiles: File[]) {
    onChange(newFiles?.[0]);
  }

  function handleOnRemove(event: SyntheticEvent) {
    event.stopPropagation();
    onChange(null);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const classes = useFileUploadStyles();
  return (
    <Card variant="outlined" className={classes.card}>
      {(title || onDownload) && (
        <CardHeader
          title={title}
          action={
            onDownload && (
              <IconButton
                color="primary"
                testId="download"
                onClick={onDownload}
                size="small"
              >
                <CloudDownload />
              </IconButton>
            )
          }
        />
      )}
      {(isNportalUser || displayPersonalInformation) && <CardContent className={title || onDownload ? classes.cardContent : undefined}>
        <Box {...getRootProps()} className={classes.dragArea}>
          <input {...getInputProps()} />
          <CloudUpload className={classes.icon} />
          {file ? (
            <Box className={classes.fileLine}>
              <Typography>{file?.name}</Typography>
              <Button
                testId={`removeFile-${file?.name}`}
                size="small"
                variant="outlined"
                className={classes.removeButton}
                onClick={handleOnRemove}
              >
                REMOVE <Close />
              </Button>
            </Box>
          ) : isDragActive ? (
            <p>Drop the file(s) here...</p>
          ) : (
            <>
              <p>Drag File to Upload</p>
              <p>or Browse file</p>
            </>
          )}
        </Box>
        <Box></Box>
      </CardContent>}
    </Card>
  );
};
